import { type ReactNode, useState } from "react";

import { Link } from "react-router-dom";

import {
  Icon,
  Popup,
  type SemanticCOLORS,
  type SemanticICONS,
} from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import { handleClickForKeyboard } from "simplydo/core/accessibility";

import type { SideNavComponentCollapsibleProps } from "./SideNavComponent";

export type SideNavLinkProps = {
  to?: string;
  external?: boolean;
  showExternalIcon?: boolean;
  onClick?: () => void;
  content: ReactNode;
  tooltip?: ReactNode;
  iconLeft?: SemanticICONS | ReactNode;
  iconLeftAlt?: ReactNode;
  iconRight?: SemanticICONS | ReactNode;
  active?: boolean;
  compact?: boolean | "very";
  inset?: boolean;
  completed?: "done" | "almost";
  rightIcon?: any;
  secondary?: boolean;
  oneLine?: boolean;
  style?: React.CSSProperties;
} & ({ to: string } | { onClick: () => void });

const StyledSideNavLink = styled.div<{
  $active?: boolean;
  $compact?: string;
  $inset?: boolean;
  $secondary?: boolean;
  $isCollapsed?: boolean;
  $oneLine?: boolean;
}>`
  .sidebar-nav-link {
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
    > div {
      display: flex;
      flex: 1;
      ${({ $oneLine }) =>
        $oneLine ?
          "white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
        : ""}
    }
    > .icons-right,
    .icons-left {
      display: inline-flex;
      flex: 0;
      min-height: 20px;
    }
    text-transform: capitalize;
    justify-content: space-between;
    width: ${({ $inset }) => ($inset ? "calc(100% - 30px)" : "100%")};
    padding-top: 10px;
    padding-left: 10px;
    padding-right: 10px;
    padding-bottom: 10px;
    font-size: ${({ $compact }) => ($compact === "very" ? "13.5px" : "15px")};
    background-color: ${({ $active }) => ($active ? "#d2dde5" : "none")};
    &:hover {
      background-color: ${({ $active }) => ($active ? "#d2dde5" : "#eef1f2")};
    }
    .icon-alt {
      margin: 0 15px 0 2.5px;
    }
    > * {
      color: ${({ $secondary }) => ($secondary ? "gray" : "black")} !important;
      display: block;
      font-weight: ${({ $active }) => ($active ? "bold" : "normal")};
    }
    border-radius: 5px;
    margin-left: ${({ $inset }) => ($inset ? "30px" : "auto")};
  }
  ${({ $isCollapsed }) =>
    $isCollapsed &&
    `
    margin: 0 !important;
  `}
`;

const createIconComponent = (
  icon: SemanticICONS | ReactNode,
  isCollapsed = false,
) => {
  if (!icon) {
    return null;
  }
  if (typeof icon === "string") {
    return (
      <Icon
        style={{ marginRight: isCollapsed ? 0 : 10, height: 19 }}
        name={icon as SemanticICONS}
      />
    );
  }
  return icon;
};

const SideNavLinkContainerComponent = ({
  children,
  external,
  to,
  onClick,
}: {
  children: JSX.Element;
  external?: boolean;
  to: string;
  onClick?: () => void;
}) => {
  if (to) {
    if (external) {
      return (
        <a href={to} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }
    return <Link to={to}>{children}</Link>;
  }
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleClickForKeyboard(onClick)}
    >
      {children}
    </div>
  );
};

const SideNavLink = ({
  to,
  content,
  tooltip,
  iconLeft,
  iconLeftAlt,
  iconRight,
  active,
  onClick,
  compact,
  inset,
  completed,
  external,
  showExternalIcon,
  rightIcon,
  isCollapsed,
  secondary,
  oneLine,
  style,
}: SideNavLinkProps & Partial<SideNavComponentCollapsibleProps>) => {
  const [colorMap] = useState<{ [key: string]: SemanticCOLORS }>({
    done: "green",
    almost: "orange",
  });
  const theme = useTheme();

  return (
    <Popup
      disabled={!theme.sizes.isComputer || (!tooltip && !isCollapsed)}
      position="right center"
      hoverable={!!tooltip}
      on={["hover", "focus"]}
      content={tooltip || content}
      trigger={
        <StyledSideNavLink
          $active={active}
          $compact={compact}
          $inset={inset}
          $isCollapsed={theme.sizes.isComputer && isCollapsed}
          $secondary={secondary}
          $oneLine={oneLine}
        >
          <SideNavLinkContainerComponent
            external={external}
            to={to}
            onClick={onClick}
          >
            <div className="sidebar-nav-link" style={style}>
              <div className="icons-left">
                {iconLeftAlt ?
                  <span className="icon-alt">{iconLeftAlt}</span>
                : createIconComponent(iconLeft, isCollapsed)}
              </div>
              <div>
                {!(theme.sizes.isComputer && isCollapsed) ? content : null}
              </div>
              {!(theme.sizes.isComputer && isCollapsed) ?
                <div className="icons-right">
                  {external || showExternalIcon ?
                    <Icon
                      name="external alternate"
                      size="small"
                      style={{ marginLeft: 5, height: 17 }}
                    />
                  : null}{" "}
                  {completed !== undefined && (
                    <Icon
                      name={
                        completed === "done" ? "check circle" : (
                          "exclamation circle"
                        )
                      }
                      style={{ marginLeft: 5, height: 20 }}
                      color={colorMap[completed] || "red"}
                    />
                  )}{" "}
                  {rightIcon && rightIcon}
                  {createIconComponent(iconRight)}
                </div>
              : null}
            </div>
          </SideNavLinkContainerComponent>
        </StyledSideNavLink>
      }
    />
  );
};

export default SideNavLink;
